// Site language. Spanish (/es) and Brazilian Portuguese (/pt) started in Oct
// 2026: Spanish-speaking countries were 17.6% of Google clicks but ~9% of
// charges, Brazil 4.2% of clicks.
//
// Two ways a page gets its language:
// - SEO pages under /es and /pt render in that language on the server, so
//   Google indexes them in it. English URLs always render English on the
//   server: they are CDN-cached (s-maxage) and must not vary by visitor.
// - Everything else (studio, modals, paywall) follows the visitor: their saved
//   choice, else their browser language. That switch happens after hydration,
//   so it never changes what the server or the CDN sends.
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { en, es, pt, type MessageKey } from "./messages";

export type Locale = "en" | "es" | "pt";
export const LOCALES: Locale[] = ["en", "es", "pt"];
export type { MessageKey };

// hreflang / <html lang> / og:locale per site locale. The Portuguese copy is
// written for Brazil but is the only Portuguese we have, so it is offered to
// every Portuguese searcher ("pt"), and the Spanish to every Spanish one.
export const LOCALE_META: Record<
  Locale,
  { hreflang: string; htmlLang: string; og: string }
> = {
  en: { hreflang: "en", htmlLang: "en", og: "en_US" },
  es: { hreflang: "es", htmlLang: "es", og: "es_LA" },
  pt: { hreflang: "pt", htmlLang: "pt-BR", og: "pt_BR" },
};

const MESSAGES: Record<Locale, Partial<Record<MessageKey, string>>> = {
  en,
  es,
  pt,
};

// A missing translation falls back to English rather than showing a key.
export function translate(
  locale: Locale,
  key: MessageKey,
  vars?: Record<string, string | number>,
) {
  let s = MESSAGES[locale]?.[key] ?? en[key] ?? key;
  if (vars)
    for (const [k, v] of Object.entries(vars))
      s = s.split(`{${k}}`).join(String(v));
  return s;
}

// Locale implied by a path or route: /es, /es/... -> "es". Anything else is English.
export function localeFromPath(path: string | undefined | null): Locale {
  const m = /^\/(es|pt)(?:[/?#]|$)/.exec(path || "");
  return m ? (m[1] as Locale) : "en";
}

export const LANG_COOKIE = "jm_lang";

function readCookie(): Locale | null {
  try {
    const m = /(?:^|;\s*)jm_lang=(en|es|pt)/.exec(document.cookie);
    return m ? (m[1] as Locale) : null;
  } catch {
    return null;
  }
}

export function browserLocale(): Locale {
  try {
    for (const l of navigator.languages?.length
      ? navigator.languages
      : [navigator.language]) {
      const p = (l || "").toLowerCase().slice(0, 2);
      if (p === "es" || p === "pt") return p;
      if (p === "en") return "en";
    }
  } catch {
    /* no navigator (SSR) */
  }
  return "en";
}

interface Ctx {
  locale: Locale;
  // True on /es and /pt pages: the language is fixed by the URL.
  fromUrl: boolean;
  // What the visitor wants (saved choice, else browser), whatever the URL.
  // "en" until mounted.
  visitorLocale: Locale;
  setLocale: (l: Locale) => void;
}
const LocaleContext = createContext<Ctx>({
  locale: "en",
  fromUrl: false,
  visitorLocale: "en",
  setLocale: () => {},
});

// For code outside React (e.g. a helper that opens a modal with a message).
// Kept in step by LocaleProvider; "en" on the server.
let currentLocale: Locale = "en";
export const getCurrentLocale = () => currentLocale;

export function LocaleProvider({
  urlLocale,
  children,
}: {
  urlLocale: Locale;
  children: React.ReactNode;
}) {
  const fromUrl = urlLocale !== "en";
  const [visitor, setVisitor] = useState<Locale>("en");
  useEffect(() => {
    setVisitor(readCookie() ?? browserLocale());
  }, []);
  const value = useMemo<Ctx>(
    () => ({
      locale: fromUrl ? urlLocale : visitor,
      fromUrl,
      visitorLocale: visitor,
      setLocale: (l) => {
        try {
          document.cookie = `${LANG_COOKIE}=${l}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
        } catch {
          /* cookies blocked */
        }
        setVisitor(l);
      },
    }),
    [fromUrl, urlLocale, visitor],
  );
  useEffect(() => {
    currentLocale = value.locale;
  }, [value.locale]);
  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export const useLocale = () => useContext(LocaleContext);

// t() for the current locale (the URL's on /es and /pt, else the visitor's).
// Pass `fixed` for content that must stay in one language, e.g. an SEO page's
// own copy.
export function useT(fixed?: Locale) {
  const { locale } = useLocale();
  const l = fixed ?? locale;
  return useMemo(
    () => (key: MessageKey, vars?: Record<string, string | number>) =>
      translate(l, key, vars),
    [l],
  );
}
